// shared-owner-dashboard.service.ts
import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, skipWhile, take, takeUntil } from 'rxjs/operators';
import { PromptingService } from '../../services';

import { SharedProfileService } from './../../../components/profile/shared-profile.service';
import {
  BreadcrumbsElement,
  NavigationMenuItem,
  TitleBreadcrumbsConfig,
} from './../../../models/owner-dashboard';
import { PortalSocketService } from '../../services/portal-socket.service';
import { setWebsiteAsReady } from '../../store/_features/website/website.actions';
import { AuthenticationService } from '../../services';
import { selectIsWebsiteReady } from '../../store/_features/website/website.selector';
import { WebsiteModel } from 'src/app/components/user-dashboard/portal/portal.model';

export enum BackSettingsType {
  STANDARD = 'standard',
  ONE_TIME = 'one_time',
}

export interface BackSettingsObject {
  route: string;
  text: string;
  refUrl?: string;
  type: BackSettingsType;
  temp?: BackSettingsObject;
}

type BreadcrumbBase =
  | 'offer'
  | 'product'
  | 'website'
  | 'funnel'
  | 'earnings'
  | 'refund'
  | 'contact'
  | 'blog'
  | 'social-links'
  | 'dashboard'
  | 'integrations';

@Injectable({
  providedIn: 'root',
})
export class SharedOwnerDashboardService implements OnDestroy {
  private readonly BACK_CONFIG_KEY = 'bcfg';
  private readonly DEFAULT_ROUTE = '/dashboard';
  private readonly destroy$ = new Subject<void>();

  // UI State
  isGuideVisible = false;
  isGuideOpen = false;
  canNavigateAll = true;
  isWebsiteReady: boolean | null = null;
  private isOnboardingListenerActive = false;

  // Navigation Configuration
  titleBreadcrumbConfig: TitleBreadcrumbsConfig = {
    title: { main: 'Welcome', sub: 'You are logged in as a Learmo Owner' },
    breadcrumb: [],
    actionsSelectors: [],
  };

  backSettings: BackSettingsObject = {
    route: this.DEFAULT_ROUTE,
    text: 'Dashboard',
    type: BackSettingsType.STANDARD,
  };

  // Audio
  private readonly websiteReadySound = new Audio('assets/notification.mp3');

  // Breadcrumb base configurations
  readonly breadcrumbsBase: Record<BreadcrumbBase, BreadcrumbsElement[]> = {
    dashboard: [{ label: 'Dashboard', url: '/' }],
    blog: [
      { label: 'Dashboard', url: '/' },
      { label: 'Blog', url: 'blog' },
    ],
    product: [
      { label: 'Dashboard', url: '/' },
      { label: 'Products', url: 'product' },
    ],
    contact: [
      { label: 'Dashboard', url: '/' },
      { label: 'Contacts', url: 'contact' },
    ],
    offer: [
      { label: 'Dashboard', url: '/' },
      { label: 'Offers', url: 'offer' },
    ],
    funnel: [
      { label: 'Dashboard', url: '/' },
      { label: 'Funnels', url: 'funnel' },
    ],
    website: [
      { label: 'Dashboard', url: '/' },
      { label: 'Website', url: 'website' },
    ],
    integrations: [
      { label: 'Dashboard', url: '/' },
      { label: 'Integrations', url: 'integrations' },
    ],

    'social-links': [
      { label: 'Dashboard', url: '/' },
      { label: 'Social Links', url: 'social-links' },
    ],
    earnings: [
      { label: 'Dashboard', url: '/' },
      { label: 'Sales & Earnings', url: 'earnings' },
    ],
    refund: [
      { label: 'Dashboard', url: '/' },
      { label: 'Refund Requests', url: 'refund' },
    ],
  };

  // Side Menu Configuration
  readonly sideMenu: NavigationMenuItem[] = [
    {
      tagId: 'dashboardTab',
      tooltip: { placement: 'right', text: 'Dashboard' },
      label: 'Dashboard',
      routerLink: '/dashboard',
      iconName: 'dashboard',
      routerLinkExact: true,
    },
    {
      tagId: 'productsTab',
      tooltip: { placement: 'right', text: 'Products' },
      label: 'Products',
      routerLink: 'product',
      iconName: 'products',
      routerLinkExact: false,
    },
    {
      tagId: 'salesTab',
      tooltip: { placement: 'right', text: 'Sales' },
      label: 'Sales & Earnings',
      expandable: { active: true, status: false },
      routerLink: 'earnings',
      iconName: 'sales',
      // subItems: [
      //   {
      //     tagId: 'earningsTab',
      //     label: 'Sales & Earnings',
      //     routerLink: 'earnings',
      //   },
      //   {
      //     tagId: 'refundTab',
      //     label: 'Refund',
      //     routerLink: 'refund',
      //   },
      // ],
      routerLinkExact: false,
    },
    {
      label: 'General',
      separator: true,
    },
    {
      tagId: 'websiteTab',
      tooltip: { placement: 'right', text: 'Website' },
      label: 'Website',
      routerLink: '/dashboard/website',
      iconName: 'website',
      expandable: { active: true, status: false },
      subItems: [
        {
          tagId: 'overviewTab',
          label: 'Overview',
          routerLink: '/dashboard/website/overview',
        },
        {
          tagId: 'identityTab',
          label: 'Site Identity',
          routerLink: '/dashboard/website/site-identity',
        },
        {
          tagId: 'designTab',
          label: 'Design & Branding',
          routerLink: '/dashboard/website/branding',
        },
        {
          tagId: 'contentTab',
          label: 'Pages',
          routerLink: '/dashboard/website/content',
        },
        {
          tagId: 'settingsTab',
          label: 'Settings',
          routerLink: '/dashboard/website/settings',
        },
      ],
      routerLinkExact: false,
    },
    {
      tagId: 'integrationsTab',
      tooltip: { placement: 'right', text: 'Integrations' },
      label: 'Integrations',
      routerLink: '/dashboard/integrations',
      iconName: 'integrations',
    },
    {
      tagId: 'blogTab',
      tooltip: { placement: 'right', text: 'Blog' },
      label: 'Blog',
      routerLink: 'blog',
      iconName: 'blog',
      routerLinkExact: false,
    },
    {
      tagId: 'socialLinksTab',
      tooltip: { placement: 'right', text: 'Social Links' },
      label: 'Social Links',
      routerLink: '/dashboard/social-links',
      iconName: 'social',
    },
    {
      tagId: 'contactsTab',
      tooltip: { placement: 'right', text: 'Contacts' },
      label: 'Contacts',
      routerLink: 'contact',
      iconName: 'contact',
      routerLinkExact: true,
    },
    // {
    //   label: 'Reports',
    //   separator: true,
    // },
    // {
    //   tagId: 'reportsTab',
    //   tooltip: { placement: 'right', text: 'Reports & Analytics' },
    //   label: 'Reports & Analytics',
    //   routerLink: 'reports',
    //   iconName: 'analytics',
    //   routerLinkExact: true,
    // },
  ];

  constructor(
    public router: Router,
    private title: Title,
    private socketService: PortalSocketService,
    private store: Store,
    private sharedProfileService: SharedProfileService,
    private authService: AuthenticationService,
    private prompting: PromptingService,
  ) {
    this.initializeWebsiteReadyListener();
    this.initializeSocketListener();
    this.initializeRouteListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize website ready state listener
   */
  private initializeWebsiteReadyListener(): void {
    this.store
      .select(selectIsWebsiteReady)
      .pipe(
        filter((value) => value !== null),
        takeUntil(this.destroy$),
      )
      .subscribe((value) => {
        this.isWebsiteReady = value;

        if (
          !this.sharedProfileService.currentUser?.onBoarding?.websiteSetting &&
          value &&
          !this.isOnboardingListenerActive
        ) {
          this.listenToOnboarding();
        }
      });
  }

  /**
   * Initialize socket ready listener
   */
  private initializeSocketListener(): void {
    this.socketService.socketReady$
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(() => {
        this.startOwnerWebsiteProcessingListening();
      });
  }

  /**
   * Initialize route change listener
   */
  private initializeRouteListener(): void {
    // Handle initial route
    this.handleRouteChange(this.router.url);

    // Subscribe to route changes
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((event: NavigationEnd) => {
        this.handleRouteChange(event.url);
      });
  }

  /**
   * Handle route changes to update guide visibility
   */
  private handleRouteChange(url: string): void {
    this.isGuideVisible = url !== '/dashboard';
  }

  /**
   * Start listening for website processing events
   */
  private startOwnerWebsiteProcessingListening(): void {
    this.socketService.website$
      .pipe(
        filter((website: WebsiteModel) => {
          // Safety: only process events for the current user's website
          if (!website?.id) return false;
          return true;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((website: WebsiteModel) => {
        this.store.dispatch(setWebsiteAsReady({ website }));

        if (
          website.isFirstTimeReady ||
          this.sharedProfileService.currentUser?.onBoarding?.websiteSetting
        ) {
          this.triggerWebsiteReadyAction(website.id);
        } else if (!this.isOnboardingListenerActive) {
          this.listenToOnboarding(website.id);
        }
      });
  }

  /**
   * Listen for onboarding completion
   */
  private listenToOnboarding(websiteId?: number): void {
    if (this.isOnboardingListenerActive) {
      return;
    }

    this.isOnboardingListenerActive = true;

    this.authService.currentUser
      .pipe(
        skipWhile((user) => !!user?.id && !user?.onBoarding?.websiteSetting),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.isOnboardingListenerActive = false;
        this.triggerWebsiteReadyAction(websiteId);
      });
  }

  /**
   * Trigger website ready notification
   */
  private triggerWebsiteReadyAction(websiteId?: number): void {
    const sessionKey = `l_w_notif_${websiteId || 'default'}`;
    if (sessionStorage.getItem(sessionKey)) {
      return;
    }

    this.playWebsiteReadySound();

    this.prompting
      .confirm(
        {
          icon: 'success',
          title: 'Your Learning Hub is Ready!',
          html: 'Continue to your dashboard to review your website, explore Learmo, and create your first course with Learmo AI.',
          confirmButtonText: 'Continue to Dashboard',
          showCancelButton: true,
        },
        'md',
      )
      .then((result) => {
        if (result.isConfirmed) {
          sessionStorage.setItem(sessionKey, 'true');
          this.router.navigate(['/dashboard']);
        }
      });
  }

  /**
   * Play website ready sound
   */
  playWebsiteReadySound(): void {
    this.websiteReadySound.play().catch((error) => {
      console.warn('Could not play sound:', error);
    });
  }

  // Sidebar Management
  expandSidebarItem(
    sideMenu: NavigationMenuItem[],
    item: NavigationMenuItem,
  ): void {
    this.collapseSidebarItems(sideMenu);

    if (item.expandable?.active) {
      item.expandable.status = true;
      setTimeout(() => {
        this.scrollTo(item.tagId, 'start');
      }, 350);
    }
  }

  collapseSidebarItem(item: NavigationMenuItem): void {
    if (item.expandable) {
      item.expandable.status = false;
    }
  }

  collapseSidebarItems(sideMenu: NavigationMenuItem[]): void {
    sideMenu.forEach((menuItem) => {
      if (menuItem?.expandable?.status) {
        menuItem.expandable.status = false;
      }
    });
  }

  scrollTo(
    elementId: string,
    position: ScrollLogicalPosition = 'nearest',
  ): void {
    const element = document.getElementById(elementId);
    element?.scrollIntoView({
      behavior: 'smooth',
      block: position,
    });
  }

  // Title and Breadcrumb Management
  setWelcomeBackMessage(config: {
    main?: string;
    firstName: string;
    role?: string;
  }): void {
    const fullMessage = `${config.main || '<span class="font-light">Welcome</span>'},
     ${config.firstName}`;

    this.setTitle({
      main: fullMessage,
      sub: 'your learning hub is live and ready to explore.',
    });

    this.clearBreadcrumbs();
    this.title.setTitle('Learmo Platform > Dashboard');
  }

  setTitle(
    titleConfig: { main?: string; sub?: string },
    actions: any[] = [],
  ): void {
    if (titleConfig.main != null) {
      this.titleBreadcrumbConfig.title.main = titleConfig.main;
      this.title.setTitle(titleConfig.main);
    }

    if (titleConfig.sub != null) {
      this.titleBreadcrumbConfig.title.sub = titleConfig.sub;
    }

    this.titleBreadcrumbConfig.actionsSelectors = actions;
  }

  clearTitle(): void {
    this.setTitle({ sub: '', main: '' });
  }

  clearActions(): void {
    this.setTitle({}, []);
  }

  clearBreadcrumbs(): void {
    this.setBreadcrumbs({ customBreadCrumbs: [] });
  }

  renameLastNodeIfExists(label: string): void {
    const breadcrumbs = this.titleBreadcrumbConfig.breadcrumb;
    if (breadcrumbs?.length) {
      breadcrumbs[breadcrumbs.length - 1].label = label;
    }
  }

  setBreadcrumbs(
    config: {
      base?: BreadcrumbBase;
      lastNode?: string;
      customBreadCrumbs?: BreadcrumbsElement[];
    } = { customBreadCrumbs: [] },
  ): void {
    if (!config.customBreadCrumbs?.length && config.base) {
      const baseClone: BreadcrumbsElement[] = JSON.parse(
        JSON.stringify(this.breadcrumbsBase[config.base]),
      );

      if (baseClone?.length) {
        if (config.lastNode) {
          baseClone.push({
            label: config.lastNode,
            url: null,
            isLastNode: true,
          });
          this.title.setTitle(config.lastNode);
        } else {
          const lastIndex = baseClone.length - 1;
          baseClone[lastIndex].url = null;
          this.title.setTitle(baseClone[lastIndex].label);
        }

        this.titleBreadcrumbConfig.breadcrumb = baseClone;
      } else {
        this.titleBreadcrumbConfig.breadcrumb = [];
      }
    } else {
      this.titleBreadcrumbConfig.breadcrumb = config.customBreadCrumbs || [];
    }
  }

  // Back Navigation Management
  goBack(): void {
    this.router.navigateByUrl(this.backSettings.route);
    this.checkBackSettings();
  }

  setBackRoute(
    text: string,
    route: string = this.router.url,
    refUrl?: string,
    type: BackSettingsType = BackSettingsType.STANDARD,
  ): void {
    const currentSettings = this.getBackRoute();
    const newSettings: BackSettingsObject = { route, text, refUrl, type };

    setTimeout(() => {
      if (type === BackSettingsType.ONE_TIME) {
        newSettings.temp = currentSettings;
      }

      localStorage.setItem(this.BACK_CONFIG_KEY, JSON.stringify(newSettings));
      this.backSettings = this.getBackRoute();
    }, 350);
  }

  setRefUrl(url: string): void {
    const backCfg = this.getBackRoute();
    if (backCfg) {
      backCfg.refUrl = url;
      localStorage.setItem(this.BACK_CONFIG_KEY, JSON.stringify(backCfg));
      this.backSettings = this.getBackRoute();
    }
  }

  private checkBackSettings(): void {
    const backSettings = this.getBackRoute();

    if (backSettings?.type === BackSettingsType.ONE_TIME && backSettings.temp) {
      this.setBackRoute(
        backSettings.temp.text,
        backSettings.temp.route,
        backSettings.temp.refUrl,
        backSettings.temp.type,
      );
    }
  }

  destroyBackRoute(): void {
    localStorage.removeItem(this.BACK_CONFIG_KEY);
  }

  getBackRoute(): BackSettingsObject | null {
    try {
      const settings = localStorage.getItem(this.BACK_CONFIG_KEY);
      return settings ? JSON.parse(settings) : null;
    } catch (error) {
      console.error('Failed to parse back route settings:', error);
      return null;
    }
  }

  // Navigation Control
  enableNavigate(): void {
    this.canNavigateAll = true;
  }

  disableNavigate(): void {
    this.canNavigateAll = false;
  }
}
